import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ASSET_NAME, Countries } from 'src/app/app.consts';
import { ApplicationService } from 'src/app/services/application-service';
import { debounceTime } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { FlowService } from 'src/app/services/flow.service';
import {
  KlarnaSessionCompleteRequestModel,
  KlarnaSessionStartResponseModel,
  KlarnaSessionTerminateRequestModel,
} from 'src/app/models/klarna.model';
import { TrackingService } from 'src/app/services/tracking.service';
import { KlarnaApi } from 'src/app/services/api/klarna-api.service';
import { environment } from 'src/environments/local/environment';
import { ApplicationScoreService } from 'creddo-shared-library';

@Component({
  selector: 'app-account-data',
  templateUrl: './account-data.component.html',
  styleUrls: ['./account-data.component.scss'],
})
export class AccountDataComponent implements OnInit, AfterViewInit, OnDestroy {
  applicationId!: number;
  accountDataForm!: FormGroup;
  error!: string;
  assetName!: typeof ASSET_NAME;
  subs: Array<Subscription> = [];
  tab = 1;
  sessionStartModel!: KlarnaSessionStartResponseModel;
  manualUploadLabel = '';
  manualUploadMoreLabel = '';
  market!: string;
  @Input() isSummary!: boolean;

  constructor(
    private formBuilder: FormBuilder,
    public applicationService: ApplicationService,
    private trackingService: TrackingService,
    private klarnaApi: KlarnaApi,
    private window: Window,
    public flowService: FlowService,
    private applicationScoreService: ApplicationScoreService
  ) {}

  ngOnInit(): void {
    this.market = environment.country;
    this.applicationId = this.applicationService.applicationId;
    const initialAccountData = this.applicationService.accountData || [];
    this.accountDataForm = this.formBuilder.group({
      accountData: this.formBuilder.array(initialAccountData),
    });
    this.assetName = ASSET_NAME;

    if (this.market === Countries.NETHERLANDS) {
      this.manualUploadLabel = 'accountData.manualUploadLabel';
      this.manualUploadMoreLabel = 'accountData.manualUploadMoreLabel';
      this.tab = 2;
    }

    this.subs.push(
      this.accountData.valueChanges.pipe(debounceTime(200)).subscribe((v) => {
        this.applicationService.accountData = v;
        this.applicationScoreService.updateAppScore(
          'accountstatement',
          Object.keys(v).length !== 0 ? true : false
        );
      })
    );
  }

  ngAfterViewInit(): void {
    this.window.onXS2AReady = () => {
      // configure once for all flows (optional)
      this.window.XS2A.configure({
        autoClose: true,
        hideTransitionOnFlowEnd: true,
        onLoad: () => {
          console.log('onLoad called');
        },
        onReady: () => {
          console.log('onReady called');
        },
        onAbort: () => {
          console.log('onAbort called');
          this.handleOnAbort();
        },
        onError: (error: any) => {
          console.log('onError called', error);
        },
        onFinished: () => {
          console.log('onFinished called');
          this.handleOnFinished();
        },
        onClose: () => {
          console.log('onClose called');
        },
      });
    };
  }

  ngOnDestroy(): void {
    this.subs.map((sub) => sub.unsubscribe());
  }

  trackFields(field: string): void {
    this.trackingService.filledField(field);
  }

  launchKlarnaXS2App(): void {
    // Track the launch
    this.trackFields('kobLaunched');
    // Start the session
    this.subs.push(
      this.klarnaApi
        .sessionStart(this.applicationId)
        .subscribe((v: KlarnaSessionStartResponseModel) => {
          this.sessionStartModel = v;
          this.window.XS2A.startFlow(v.clientToken, {
            onLoad: () => {
              console.log('onLoad called #2');
            },
            onFinished: () => {
              // Read the flow from the server to retrieve the account list.
              console.log(
                'onFinished: read the flow from the server to retrieve the account list.'
              );
              this.handleOnFinished();
            },
            onAbort: () => {
              console.log('onAbort called');
              this.handleOnAbort();
            },
            onError: (error: any) => {
              console.error(
                'onError: something bad happened during the flow.',
                error
              );
              this.handleOnError();
            },
          });
        })
    );
  }

  handleOnFinished(): void {
    const sessionCompleteModel = {} as KlarnaSessionCompleteRequestModel;
    sessionCompleteModel.applicationId = this.applicationId;
    sessionCompleteModel.clientToken = this.sessionStartModel.clientToken;
    sessionCompleteModel.flowUrl = this.sessionStartModel.flowUrl;
    sessionCompleteModel.sessionUrl = this.sessionStartModel.sessionUrl;
    this.klarnaApi.sessionComplete(sessionCompleteModel);
    this.trackFields('kobCompleted');
    this.applicationService.klarnaUpload = true;
  }

  handleOnAbort(): void {
    const sessionTerminateModel = {} as KlarnaSessionTerminateRequestModel;
    sessionTerminateModel.applicationId = this.applicationId;
    sessionTerminateModel.sessionUrl = this.sessionStartModel.sessionUrl;
    this.klarnaApi.sessionTerminate(sessionTerminateModel);
    this.trackFields('kobAborted');
  }

  handleOnError(): void {
    const sessionTerminateModel = {} as KlarnaSessionTerminateRequestModel;
    sessionTerminateModel.applicationId = this.applicationId;
    sessionTerminateModel.sessionUrl = this.sessionStartModel.sessionUrl;
    // TODO Show error to client
    this.klarnaApi.sessionTerminate(sessionTerminateModel);
    this.trackFields('kobError');
  }

  handleBackButtonClick(): void {
    this.flowService.previousScreen();
  }

  handleNextButtonClick(): void {
    this.flowService.nextScreen();
  }

  get form(): FormGroup {
    return this.accountDataForm;
  }

  get accountData(): FormArray {
    return this.form.get('accountData') as FormArray;
  }
}
